import { useEffect, useSyncExternalStore } from 'react';
import { getConsentMode, subscribeConsentMode } from './consentMode';

/**
 * Host-relayed consent for visitor identification (IX-5465).
 *
 * Sites with `qualification.identification_requires_consent` identify nobody until the
 * host's cookie banner reports consent. The value lives on `window` so a host can set it
 * before the widget bundle has loaded; `setIdentificationConsent` (exposed as
 * `window.InboundXWidget.setIdentificationConsent`) writes it and re-renders the widget.
 * The latest call wins: `false` after `true` stops any new identification.
 */
export const IDENTIFICATION_CONSENT_GLOBAL = '__INBOUNDX_IDENTIFICATION_CONSENT__';

type ConsentWindow = Window & { [IDENTIFICATION_CONSENT_GLOBAL]?: unknown };

const listeners = new Set<() => void>();

// The site's flag, mirrored by useIdentificationAllowed for code outside React
// (Vector callbacks, Snitcher flush, request bodies). Consent is per page, so once any
// widget on the page requires it, it stays required.
let consentRequired = false;

/** `true` / `false` once the host has reported, `null` while it has not. */
export function getIdentificationConsent(): boolean | null {
    if (typeof window === 'undefined') return null;
    const value = (window as ConsentWindow)[IDENTIFICATION_CONSENT_GLOBAL];
    return typeof value === 'boolean' ? value : null;
}

export function setIdentificationConsent(granted: boolean): void {
    if (typeof window === 'undefined' || typeof granted !== 'boolean') return;
    (window as ConsentWindow)[IDENTIFICATION_CONSENT_GLOBAL] = granted;
    // Always notify: the host may have set the global directly before calling this, and
    // React ignores a notification whose snapshot did not change.
    for (const listener of listeners) listener();
}

/** Synchronous gate for code outside React: same rule as useIdentificationAllowed. */
export function isIdentificationAllowed(): boolean {
    return getConsentMode() === 'full' && (!consentRequired || getIdentificationConsent() === true);
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** Whether identification may run: always without the site flag, else only on `true`. */
export function useIdentificationAllowed(requiresConsent: boolean | undefined): boolean {
    const consent = useSyncExternalStore(subscribe, getIdentificationConsent, () => null);
    const mode = useSyncExternalStore(subscribeConsentMode, getConsentMode, () => 'pending');
    useEffect(() => {
        if (requiresConsent === true) consentRequired = true;
    }, [requiresConsent]);
    return mode === 'full' && (requiresConsent !== true || consent === true);
}
